/**
 * Route Protection Middleware
 * Blocks access to routes not in user's menu permissions from backend
 */

import { Lock } from '@lucide/vue'
import { navigateTo } from '#app'
import { useIdentity } from '~/composables/useIdentity'
import { useLogger } from '~/composables/useLogger'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { holdsCustomerRole } from '~/utils/holdsCustomerRole'
import { Normalization } from '~/utils/normalization'

const isRecord = Normalization.isRecord

const logger = useLogger('RouteProtectionMiddleware')

// Only a well-formed numeric store id can be a store created elsewhere.
const STORE_ID_ROUTE = /^\/dashboard\/stores\/\d+(?:\/|$)/
const VERIFY_EMAIL_PATH = '/verify-email'

const isUnauthorizedAccessError = (error: unknown): boolean => {
  if (!isRecord(error)) {
    return false
  }

  if (error['statusCode'] === 401) {
    return true
  }

  return isRecord(error['response']) && error['response']['status'] === 401
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  // Only run on client side
  if (import.meta.server) return

  const userStore = useUserStore()
  const errorStore = useErrorStore()
  const { isAuthenticated, identity, roles } = useIdentity()
  const t = usePresentationTranslator()

  const isVerifyEmailRoute = to.path === VERIFY_EMAIL_PATH

  // The verify-email route can select the dashboard layout for partner
  // sessions, so it needs the same access initialization and error boundary.
  if (!to.path.startsWith('/dashboard') && !isVerifyEmailRoute) {
    return
  }

  // Check authentication first - redirect to login if not authenticated
  if (!isAuthenticated.value || !identity.value) {
    if (isVerifyEmailRoute) return

    return navigateTo(`/login?returnUrl=${encodeURIComponent(to.fullPath)}`)
  }

  const roleNames = roles.value

  // Customers keep the landing layout on verify-email and do not need
  // dashboard access initialization.
  if (isVerifyEmailRoute && holdsCustomerRole(roleNames)) {
    return
  }

  const showAccessVerificationError = () => {
    errorStore.setError({
      type: '403',
      title: 'Access Verification Failed',
      message: () => t('auth.routeProtection.accessVerificationFailed'),
      icon: Lock,
      primaryAction: {
        label: 'Reload',
        onClick: () => {
          window.location.reload()
        }
      },
      secondaryAction: {
        label: 'Go to Login',
        onClick: () => {
          void Promise.resolve(navigateTo('/login')).catch((navigationError: unknown) => {
            logger.error('redirectToLogin', { navigationError })
          })
        }
      }
    })
  }

  // Load user access if not loaded
  if (!userStore.initialized) {
    try {
      await userStore.fetchUserAccess()
    } catch (error: unknown) {
      // Check if it's a 401 error (session expired)
      if (isUnauthorizedAccessError(error)) {
        userStore.clearUserState()
        return navigateTo(`/login?returnUrl=${encodeURIComponent(to.fullPath)}`)
      }

      showAccessVerificationError()

      // Let verify-email finish entering its dashboard layout so its
      // PageErrorBoundary can render the diagnostic. Dashboard-to-dashboard
      // navigation keeps the existing abort behavior.
      return isVerifyEmailRoute ? undefined : abortNavigation()
    }

    // The user store records access failures without rethrowing. Detect that
    // state explicitly so verify-email cannot mount a silently empty sidebar.
    if (!userStore.initialized) {
      showAccessVerificationError()
      return isVerifyEmailRoute ? undefined : abortNavigation()
    }
  }

  // Verify-email needs initialized dashboard data, but it is not itself a
  // permissioned dashboard route.
  if (isVerifyEmailRoute) {
    return
  }

  // Check access BEFORE navigation completes
  const canAccessRoute = () => checkRouteAccess(to.path, userStore.access.permissions, roleNames)
  const canAccessStore = () => canAccessStoreRoute(to.path, userStore.access.context.stores)

  // A store created elsewhere is absent from the cached list: refetch once before denying.
  if (canAccessRoute() && !canAccessStore() && STORE_ID_ROUTE.test(to.path)) {
    await userStore.fetchUserAccess(true)
  }

  const hasAccess = canAccessRoute() && canAccessStore()

  if (!hasAccess) {
    // Set error state for PageErrorBoundary to display
    errorStore.setError({
      type: '403',
      title: 'Access Denied',
      message: () => t('auth.routeProtection.accessDenied'),
      icon: Lock,
      // primaryAction: {
      //   label: 'Go to Dashboard',
      //   onClick: () => navigateTo('/dashboard')
      // },
      secondaryAction: {
        label: 'Go Back',
        onClick: () => {
          errorStore.clearError()
          if (from.path.length > 0 && from.path !== to.path) {
            void Promise.resolve(navigateTo(from.path)).catch((navigationError: unknown) => {
              logger.error('redirectBack', { navigationError })
            })
          } else {
            void Promise.resolve(navigateTo('/dashboard')).catch((navigationError: unknown) => {
              logger.error('redirectToDashboard', { navigationError })
            })
          }
        }
      }
    })

    // CRITICAL: Just abort navigation without throwing fatal error
    // This allows PageErrorBoundary in dashboard layout to show the error
    // instead of showing the global error.vue page
    return abortNavigation()
  }
})
